import Router from 'vue-router'
import VueCookies from 'vue-cookies'
import store from '@/stores'
import { decideNavigation } from '@/router/guard'

function isNavigationFailure(err) {
	if (Router.isNavigationFailure && Router.isNavigationFailure(err)) return true
	const msg = (err && err.message) || ''
	return /NavigationDuplicated|Avoided redundant navigation|Navigation cancelled|Navigation aborted/i.test(msg)
}

const originalPush = Router.prototype.push
const originalReplace = Router.prototype.replace

Router.prototype.push = function push(location, onResolve, onReject) {
	if (onResolve || onReject) return originalPush.call(this, location, onResolve, onReject)
	return originalPush.call(this, location).catch(err => {
		if (isNavigationFailure(err)) return err
		return Promise.reject(err)
	})
}

Router.prototype.replace = function replace(location, onResolve, onReject) {
	if (onResolve || onReject) return originalReplace.call(this, location, onResolve, onReject)
	return originalReplace.call(this, location).catch(err => {
		if (isNavigationFailure(err)) return err
		return Promise.reject(err)
	})
}

/**
 * 개발 전용 «시험양식 관리» 진입 가드.
 * 권한이 아직 적재되지 않았으면 먼저 맞춰 본다 — 새로고침 직후 can() 이
 * false 라 정상 사용자가 튕기는 것을 막는다.
 */
async function guardDevOnlyReportForm(to, from, next) {
	try {
		await store.dispatch('user/syncPermissionVersion')
	} catch (e) {
		// 권한 조회 실패는 적재된 값 기준으로 판단
	}
	if (store.getters['user/can']('admin.report.manage')) return next()
	alert('해당 페이지에 접근할 권한이 없습니다.')
	return next(from.name ? false : '/')
}

const router = new Router({
	mode: 'history',
	base: process.env.BASE_URL,
	routes: [
		{
			path: '/',
			name: "DashboardMain",
			props: true,
			meta: { pageKey: 'home' },
			component: () => import(
				/* webpackChunkName: "client list" */ 
				'@/views/dashboard/pages/index.vue'
			),
		},
		{
			path: '/dashboard/analysis',
			name: "DashboardAnalysis",
			props: true,
			meta: { pageKey: 'dashboard.analysis' },
			component: () => import(
				/* webpackChunkName: "client list" */ 
				'@/views/dashboard/pages/analysis.vue'
			),
		},
		{
			path: '/dashboard/admin',
			name: "DashboardAdmin",
			props: true,
			meta: { pageKey: 'admin' },
			component: () => import(
				/* webpackChunkName: "client list" */ 
				'@/views/dashboard/pages/admin.vue'
			),
		},
		{
			path: '/client/list',
			name: "ClientList",
			props: true,
			meta: { pageKey: 'client' },
			component: () => import(
				/* webpackChunkName: "client list" */ 
				'@/views/client/pages/list.vue'
			),
		},
		{
			path: '/client/form/:mode',
			name: "ClientEdit",
			props: true,
			meta: { pageKey: 'client' },
			component: () => import(
				/* webpackChunkName: "client edit" */ 
				'@/views/client/pages/edit.vue'
			),
		},
		{
			path: '/client/detail/:id',
			name: 'ClientDetail',
			props: true,
			meta: { pageKey: 'client' },
			component: () => import('@/views/client/pages/detail.vue')
		},
		{
			path: '/project/list',
			name: "ProjectList",
			props: true,
			meta: { pageKey: 'project' },
			component: () => import(
				/* webpackChunkName: "client list" */ 
				'@/views/project/pages/list.vue'
			),
		},
		{
			path: '/project/detail/:id',
			name: 'ProjectDetail',
			props: true,
			meta: { pageKey: 'project' },
			component: () => import('@/views/project/pages/detail.vue')
		},
		{
			path: '/project/form/:mode',
			name: "ProjectEdit",
			props: true,
			meta: { pageKey: 'project' },
			component: () => import(
				/* webpackChunkName: "project edit" */ 
				'@/views/project/pages/edit.vue'
			),
		},
		{
			path: '/project/excelUpload',
			name: 'ProjectExcelUpload',
			props: true,
			meta: { pageKey: 'project.excelUpload' },
			component: () => import(
				/* webpackChunkName: "project excel upload" */
				'@/views/project/pages/excelUpload.vue'
			),
		},
		{
			path: '/project/:id/journal/:mode',
			name: "ProjectJournal2",
			props: true,
			meta: { pageKey: 'project' },
			component: () => import(
				/* webpackChunkName: "project edit" */ 
				'@/views/project/pages/journal2.vue'
			),
		},
		{
			path: '/project/:id/journal2/:mode',
			name: "ProjectJournal",
			props: true,
			meta: { pageKey: 'project' },
			component: () => import(
				/* webpackChunkName: "project edit" */ 
				'@/views/project/pages/journal.vue'
			),
		},
		{
			path: '/project/:id/specimen/:mode',
			name: "ProjectSpecimen",
			props: true,
			meta: { pageKey: 'project' },
			component: () => import(
				/* webpackChunkName: "project edit" */ 
				'@/views/project/pages/specimen.vue'
			)
		},
		{
			path: '/analysis/list',
			name: "AnalysisList",
			props: true,
			meta: { pageKey: 'analysis' },
			component: () => import(
				/* webpackChunkName: "analysis list" */ 
				'@/views/analysis/pages/list.vue'
			),
		},
		{
			path: '/member/list',
			name: "MemberList",
			props: true,
			component: () => import(
				/* webpackChunkName: "member list" */ 
				'@/views/member/pages/list.vue'
			),
		},
		{
			path: '/login',
			name: 'Signin',
			meta: { public: true }, // 비로그인 접근 허용 (로그인)
			props: true,
			component: () => import(
				/*webpackChunkName: "login page"*/
				'@/views/login/signin.vue'
			)
		},
		{
			path: '/logout',
			name: 'Logout',
			meta: { public: true }, // 비로그인 접근 허용 (로그아웃)
			props: true,
			component: () => import(
				/*webpackChunkName: "login page"*/
				'@/views/login/logout.vue'
			)
		},
		{
			path: '/login/email',
			name: 'SubmitEmail',
			meta: { public: true }, // 비로그인 접근 허용 (비밀번호 재설정 메일)
			props: true,
			component: () => import(
				/*webpackChunkName: "login page"*/
				'@/views/login/email.vue'
			)
		},
		{
			path: '/login/reset',
			name: 'ResetPw',
			meta: { public: true }, // 비로그인 접근 허용 (비밀번호 재설정)
			props: true,
			component: () => import(
				/*webpackChunkName: "login page"*/
				'@/views/login/reset.vue'
			)
		},
		/**
		 * 시험양식(시험일지·성적서 양식) 편집 — **개발 전용 도구**.
		 *
		 * 메뉴에 링크가 없어 URL 로만 들어오는데, pageKey 가 없으면 라우터 가드가
		 * 통과시키므로 종전에는 **로그인한 누구나 열 수 있었다**. 서버 API 만
		 * admin.report.manage 로 막혀 있어 화면은 뜨고 저장만 403 이 났다.
		 * 사용자에게 아예 보이지 않아야 하므로 진입 자체를 막는다.
		 * (세부 권한 설정에 노출하지 않는 숨김 기능이라 pageKey 대신 beforeEnter 를 쓴다)
		 */
		{
			path: '/project/confirmForm',
			name: 'ConfirmForm',
			props: true,
			beforeEnter: guardDevOnlyReportForm,
			component: () => import(
				'@/views/project/pages/confirmForm.vue'
			)
		},
		{
			path: '/project/confirmForm/detail',
			name: 'ConfirmFormDetail',
			props: true,
			beforeEnter: guardDevOnlyReportForm,
			component: () => import(
				'@/views/project/pages/confirmFormData.vue'
			)
		},
		//정산. 2026.01.29 추가.
		{
			path: '/settlement/list',
			name: 'SettlementList',
			props: true,
			meta: { pageKey: 'settlement' },
			component: () => import(
				/* webpackChunkName: "settlement list" */ 
				'@/views/settlement/pages/list.vue'
			)
		}
	],
})

router.beforeEach(async (to, from, next) => {
	const pageKey = to.matched
		.map(record => record.meta?.pageKey)
		.filter(Boolean)
		.pop()

	// 공개 페이지(meta.public)만 즉시 통과.
	// 종전에는 pageKey 가 없으면 공개로 간주해, 권한 키를 안 붙인 화면이
	// 인증 검사까지 건너뛰고 비로그인 상태로 렌더됐다.
	const isPublic = to.matched.some(record => record.meta?.public === true)
	if (isPublic) {
		next()
		return
	}

	// 토큰 없으면 데이터 로딩 없이 로그인으로
	const hasToken = !!VueCookies.get('token')
	if (hasToken) {
		// 프로필 적재 (권한도 함께 dispatch)
		const profile = store.getters['user/profile']
		if (!profile?.id) {
			try {
				await store.dispatch('user/getProfile')
			} catch (e) {
				next('/login')
				return
			}
		}
		// 역할 기반 권한 적재 + 매트릭스 변경 시 version 비교 재조회
		try {
			await store.dispatch('user/syncPermissionVersion')
		} catch (e) {
			// 권한 조회 실패는 무시(적재된 값 기준 판단)
		}
	}

	const decision = decideNavigation({
		pageKey,
		isPublic,
		hasToken,
		canAccessPage: store.getters['user/canAccessPage'],
		fromName: from.name
	})

	if (decision.type === 'allow') {
		next()
	} else if (decision.type === 'redirect') {
		next(decision.to)
	} else {
		// deny
		alert('해당 페이지에 접근할 권한이 없습니다.')
		next(decision.to)
	}
})

// 신규 배포로 인해 옛 hash chunk가 404일 때 1회만 hard reload 하여 복구
router.onError((error) => {
	const msg = (error && error.message) || ''
	const isChunkLoadError =
		/Loading chunk [\w-]+ failed/i.test(msg) ||
		/Loading CSS chunk [\w-]+ failed/i.test(msg) ||
		(error && error.name === 'ChunkLoadError')

	if (!isChunkLoadError) return

	const RELOAD_KEY = '__chunk_reload_ts'
	const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0)
	if (Date.now() - last > 10000) {
		sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
		window.location.reload()
	}
})

export default router